import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Square,
  ChevronRight,
  Zap,
} from 'lucide-react';
import type { ExecutionJob } from '../../api/coder';

interface ExecutionsTabProps {
  executions: ExecutionJob[];
  loading: boolean;
  onViewExecution: (batchId: string) => void;
  onCancelExecution: (batchId: string) => void;
  sprintStatus?: string;
}

function statusBadgeClass(status: string): string {
  const s = status.toUpperCase();
  switch (s) {
    case 'COMPLETED': case 'SUCCESS': return 'bg-accent-100 text-accent-700';
    case 'RUNNING': case 'BUILDING': return 'bg-coral-100 text-coral-700';
    case 'FAILED': case 'FAILURE': return 'bg-red-100 text-red-700';
    default: return 'bg-neutral-100 text-neutral-600';
  }
}

export function ExecutionsTab({ executions, loading, onViewExecution, onCancelExecution, sprintStatus }: ExecutionsTabProps) {
  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin text-neutral-400" />
        <p className="text-xs text-neutral-500">Loading executions...</p>
      </div>
    );
  }

  if (executions.length === 0) {
    const isTerminal = sprintStatus === 'completed' || sprintStatus === 'failed';
    return (
      <div className="h-28 flex flex-col items-center justify-center border-2 border-dashed border-neutral-200 rounded-lg">
        <Zap className="w-6 h-6 text-neutral-300 mb-1.5" />
        <p className="text-xs text-neutral-400">No executions</p>
        <p className="text-[9px] text-neutral-300 mt-0.5">
          {isTerminal
            ? 'Execution records cleaned up after build. Check Machines tab for historical fleet data.'
            : 'Start a code execution from the Start Build tab'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {executions.map((execution) => (
        <div
          key={execution.batchId}
          className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-3 cursor-pointer hover:shadow-warm transition-all"
          onClick={() => onViewExecution(execution.batchId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {execution.status === 'RUNNING' && (
                <RefreshCw className="w-4 h-4 animate-spin text-coral-500" />
              )}
              {execution.status === 'COMPLETED' && <CheckCircle className="w-4 h-4 text-accent-500" />}
              {execution.status === 'FAILED' && <XCircle className="w-4 h-4 text-red-500" />}
              {execution.status === 'CANCELLED' && <Square className="w-4 h-4 text-neutral-400" />}
              <div>
                <h4 className="text-sm font-medium text-neutral-800">{execution.batchId}</h4>
                <p className="text-[11px] text-neutral-500">
                  {execution.progress?.message || execution.status}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {execution.status === 'RUNNING' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelExecution(execution.batchId);
                  }}
                  className="px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                >
                  Cancel
                </button>
              )}
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusBadgeClass(execution.status)}`}>
                {execution.status}
              </span>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
            </div>
          </div>
          {execution.progress && execution.status === 'RUNNING' && (
            <div className="mt-2.5">
              <div className="h-1.5 rounded-full overflow-hidden bg-neutral-100">
                <div
                  className="h-full rounded-full transition-all bg-coral-500"
                  style={{ width: `${execution.progress.progress}%` }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center gap-4 mt-2 text-[10px] text-neutral-400">
            <span>Started: {new Date(execution.startTime).toLocaleString()}</span>
            {execution.closeTime && (
              <span>Finished: {new Date(execution.closeTime).toLocaleString()}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
